import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Copy, Film, Image as ImageIcon } from "lucide-react";
import { Card, PageHeader, Field, inputCls, Btn, CheckboxGroup, Empty } from "../../components/dash/Kit";
import UsageMeter, { useUsage } from "../../components/dash/UsageMeter";
import VerifyNotice from "../../components/dash/VerifyNotice";
import AiNotice from "../../components/dash/AiNotice";
import CaptionEditor from "../../components/dash/CaptionEditor";
import { api, errMessage } from "../../lib/api";

const PLATFORMS = ["Instagram", "Facebook", "LinkedIn", "TikTok"];

export default function PhotoPost() {
  const { usage, reloadUsage } = useUsage();
  const [photos, setPhotos] = useState(null);
  const [selected, setSelected] = useState("");
  const [preview, setPreview] = useState("");
  const [note, setNote] = useState("");
  const [platforms, setPlatforms] = useState(["Instagram"]);
  const [wantReel, setWantReel] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api
      .get("/media")
      .then((r) => setPhotos((r.data || []).filter((m) => (m.mime_type || "").startsWith("image/"))))
      .catch((e) => {
        console.error("Could not load your media library", e);
        setPhotos(false);
      });
  }, []);

  const pick = async (id) => {
    setSelected(id);
    setPreview("");
    try {
      const { data } = await api.get(`/media/${id}`);
      setPreview(data.data_url || "");
    } catch (e) {
      console.error("Could not load that photo", e);
    }
  };

  const generate = async () => {
    if (!selected) {
      toast.error("Pick one of your photos first.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/engine/photo-post", {
        media_id: selected,
        note,
        platforms,
        want_reel: wantReel,
      });
      setResult(data);
      reloadUsage();
      toast.success("Your photo has been turned into content.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const updateCaption = (id, caption) =>
    setResult((r) => ({
      ...r,
      items: r.items.map((i) => (i.id === id ? { ...i, caption } : i)),
      reel: r.reel && r.reel.id === id ? { ...r.reel, caption } : r.reel,
    }));

  const copy = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success("Copied.");
  };

  return (
    <div data-testid="photo-post-page">
      <PageHeader
        testId="photo-post-header"
        title="Turn a photo into a post"
        body="Pick a photo you took. PostMint looks at it, writes the caption for every platform you choose, and gives you a short reel script to film."
      />

      <div className="mb-6">
        <UsageMeter usage={usage} />
      </div>

      <AiNotice className="mb-6" testId="photo-post-ai-notice" />

      <Card testId="photo-post-picker-card">
        <p className="font-display text-lg font-semibold text-slate-900">Your photos</p>
        {photos === null && <p className="mt-3 text-sm text-slate-500">Loading your photos…</p>}
        {photos === false && <p className="mt-3 text-sm text-red-600">We couldn't load your media library.</p>}
        {Array.isArray(photos) && photos.length === 0 && (
          <div className="mt-4">
            <Empty
              testId="photo-post-empty"
              title="No photos yet"
              body="Upload a few photos of your food, products, team or space and they'll show up here."
            />
            <Link
              to="/dashboard/media"
              data-testid="photo-post-upload-link"
              className="mt-4 inline-flex rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white"
            >
              Upload photos
            </Link>
          </div>
        )}
        {Array.isArray(photos) && photos.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5" data-testid="photo-post-grid">
            {photos.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => pick(m.id)}
                data-testid={`photo-option-${m.id}`}
                className={`flex flex-col gap-1 rounded-2xl border p-2 text-left transition-colors ${
                  selected === m.id ? "border-[#F95738] bg-[#FFF3EF]" : "border-slate-200 hover:border-slate-400"
                }`}
              >
                <span className="flex h-20 items-center justify-center rounded-xl bg-slate-100 text-slate-400">
                  <ImageIcon className="h-6 w-6" aria-hidden="true" />
                </span>
                <span className="truncate text-xs font-semibold text-slate-700">{m.filename}</span>
                <span className="truncate text-[11px] text-slate-400">{m.category || "Uncategorised"}</span>
              </button>
            ))}
          </div>
        )}

        {preview && (
          <img
            src={preview}
            alt="Selected photo"
            data-testid="photo-post-preview"
            className="mt-5 max-h-64 w-auto rounded-2xl border border-slate-200 object-contain"
          />
        )}

        <div className="mt-6 space-y-5">
          <Field label="Anything we should know about this photo?" hint="Example: “This is our new spicy burger, €14.95.”">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              data-testid="photo-post-note-input"
              className={inputCls}
            />
          </Field>

          <Field label="Where should it go?">
            <CheckboxGroup name="photo-platforms" options={PLATFORMS} value={platforms} onChange={setPlatforms} />
          </Field>

          <label className="flex items-center gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={wantReel}
              onChange={(e) => setWantReel(e.target.checked)}
              data-testid="photo-post-reel-toggle"
              className="h-4 w-4 accent-[#F95738]"
            />
            Also write me a short reel script to film
          </label>

          <Btn onClick={generate} disabled={busy || !selected} testId="photo-post-generate-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {busy ? "Looking at your photo…" : "Turn this photo into content"}
          </Btn>
        </div>
      </Card>

      {result && (
        <div className="mt-6 space-y-5" data-testid="photo-post-result">
          <Card testId="photo-post-analysis">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">What we see in your photo</p>
            <p className="mt-2 text-sm text-slate-800">{result.photo_description || "—"}</p>
            {result.suggested_pillar && (
              <p className="mt-2 text-sm text-slate-500">Fits your theme: {result.suggested_pillar}</p>
            )}
            {(result.photo_tips || []).length > 0 && (
              <ul className="mt-3 space-y-1 text-sm text-slate-600" data-testid="photo-post-tips">
                {result.photo_tips.map((t) => (
                  <li key={t}>· {t}</li>
                ))}
              </ul>
            )}
          </Card>

          <VerifyNotice items={result.verify} testId="photo-post-verify" />

          {result.items.map((item) => (
            <Card key={item.id} testId={`photo-post-${item.platform.toLowerCase()}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-display text-lg font-semibold text-slate-900">{item.platform}</p>
                <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                  AI Draft
                </span>
              </div>
              <div className="mt-4">
                <CaptionEditor
                  value={item.caption}
                  onChange={(v) => updateCaption(item.id, v)}
                  onUsage={reloadUsage}
                  testIdPrefix={`photo-caption-${item.platform.toLowerCase()}`}
                />
              </div>
              <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                {[["Hashtags", item.hashtags], ["Call to action", item.cta]].map(([label, value]) =>
                  value ? (
                    <div key={label} className="rounded-xl bg-[#FAFAFA] p-3">
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
                      <dd className="mt-1 text-sm text-slate-700">{value}</dd>
                    </div>
                  ) : null
                )}
              </dl>
              <Btn
                variant="outline"
                className="mt-4"
                onClick={() => copy(`${item.caption}\n\n${item.hashtags || ""}`.trim())}
                testId={`photo-copy-${item.platform.toLowerCase()}`}
              >
                <Copy className="h-4 w-4" /> Copy caption
              </Btn>
            </Card>
          ))}

          {result.reel?.reel_script && <ReelCard reel={result.reel} onCopy={copy} />}

          <p className="text-sm text-slate-500">
            Everything here is saved in{" "}
            <Link to="/dashboard/content" className="font-semibold text-[#F95738] hover:underline">
              My Content
            </Link>
            , where you can approve it.
          </p>
        </div>
      )}
    </div>
  );
}

const ReelCard = ({ reel, onCopy }) => {
  const s = reel.reel_script;
  const full = [s.hook, s.voiceover, s.cta].filter(Boolean).join("\n\n");
  return (
    <Card testId="photo-post-reel">
      <div className="flex items-center gap-2">
        <Film className="h-5 w-5 text-[#F95738]" aria-hidden="true" />
        <p className="font-display text-lg font-semibold text-slate-900">Your reel script</p>
      </div>
      {s.hook && (
        <div className="mt-4 rounded-xl bg-[#FFF3EF] p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-[#B93615]">Hook</p>
          <p className="mt-1 text-sm text-slate-800">{s.hook}</p>
        </div>
      )}
      {(s.shots || []).length > 0 && (
        <ol className="mt-4 space-y-2" data-testid="photo-post-reel-shots">
          {s.shots.map((shot, i) => (
            <li key={`${i}-${shot.shot}`} className="rounded-xl border border-slate-200 p-3 text-sm">
              <p className="font-semibold text-slate-900">
                Shot {i + 1} · {shot.seconds ? `${shot.seconds}s` : "short"}
              </p>
              <p className="mt-1 text-slate-700">{shot.shot}</p>
              {shot.on_screen_text && (
                <p className="mt-1 text-slate-500">On screen: “{shot.on_screen_text}”</p>
              )}
            </li>
          ))}
        </ol>
      )}
      {s.voiceover && (
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">What to say</p>
          <p className="mt-1 whitespace-pre-line text-sm text-slate-800">{s.voiceover}</p>
        </div>
      )}
      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        {[["Sound", s.audio_suggestion], ["Call to action", s.cta]].map(([label, value]) =>
          value ? (
            <div key={label} className="rounded-xl bg-[#FAFAFA] p-3">
              <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
              <dd className="mt-1 text-sm text-slate-700">{value}</dd>
            </div>
          ) : null
        )}
      </dl>
      <Btn variant="outline" className="mt-4" onClick={() => onCopy(full)} testId="photo-post-copy-reel">
        <Copy className="h-4 w-4" /> Copy script
      </Btn>
    </Card>
  );
};
